<template>
    <div>
        <ui-select-v2
            v-if="show.teamInput()"
            v-model="form.team_id"
            :data="options.team"
            :loading="loader.team"
            :disabled="disableTeam || disable"
            label="Equipe:"
            item-label="label"
            return-value="value"
            @input="handleManager"
        />

        <ui-select-v2
            v-if="show.managerInput()"
            v-model="form.manager_id"
            :data="options.manager"
            :loading="loader.manager"
            :disabled="disableManager || disable"
            label="Gerente:"
            item-label="label"
            return-value="value"
            @input="handleBroker"
        />

        <ui-select-v2
            v-model="form.broker_id"
            :data="options.broker"
            :loading="loader.broker"
            :disabled="disableBroker || disable"
            label="Corretor:"
            item-label="label"
            return-value="value"
        />
    </div>
</template>

<script>
	import { uiSelectV2 } from '@/components/ui/form'
	import { team } from '@/services/axios'

	import { utilMixin, formMixin, alertMixin, loaderMixin, permissionsMixin } from '@/mixins'

	import { mapGetters } from 'vuex'

	const defaultForm = () => ({
		team_id: null,
		manager_id: null,
		broker_id: null,
	})

	const defaultLoader = () => ({
		team: false,
		manager: false,
		broker: false,
	})

	const defaultDisabled = () => ({})

	const defaultOptions = () => ({
		team: [],
		manager: [],
		broker: [],
	})

	export default {
		name: 'ui-nugget-responsible-form',

		components: {
			uiSelectV2
		},

		mixins: [utilMixin, formMixin, alertMixin, loaderMixin, permissionsMixin],

		props: {
			value: {
				type: Object,
				required: true,
			},

			fetchList: {
				type: Object,
				default: () => ({})
			},

			nuggetId: {
				type: [String, Number],
				default: ''
			},

			disable: {
				type: Boolean,
				required: false
			},
		},

		data() {
			return {
				form: { ...defaultForm() },
				options: { ...defaultOptions() },
				disabled: { ...defaultDisabled() },
				loader: { ...defaultLoader() },
				nuggetName: 'nugget-responsible-form'
			}
		},

		computed: {
			...mapGetters('auth', { user: 'getUser' }),

			show() {
				const bool = !(this.$mx_userIsManager || this.$mx_userIsBroker)
				const teamInput = () => bool
				const managerInput = () => bool
				return { teamInput, managerInput }
			},

			disableTeam() {
				return this.options.team.length == 0
			},

			disableManager() {
				return !this.$mx_hasValue(this.form.team_id) || this.options.manager.length == 0
			},

			disableBroker() {
				return (
					!this.$mx_hasValue(this.form.team_id) ||
					!this.$mx_hasValue(this.form.manager_id) ||
					this.options.broker.length == 0
				)
			},

			nuggetTotalName() {
				return this.nuggetName + (this.nuggetId ? '-' + this.nuggetId : '')
			},
		},

		watch: {
			options: {
				deep: true,
				handler(newValue) {
					this.$emit('update:fetchList', newValue)
				},
			},

			form: {
				deep: true,
				handler(newValue) {
					this.$emit('input', newValue)
				},
			},
		},

		mounted() {
			this.$root.$on(this.nuggetTotalName, async (payload = null) => {
				if (payload) {
					const { team_id, manager_id, broker_id } = payload

					if (this.show.teamInput()) this.options.team = await this.getTeams()
					if (team_id && this.show.managerInput()) await this.handleManager(team_id)
					if (manager_id) await this.handleBroker(manager_id)

					setTimeout(() => {
						// Select this team
						this.form.team_id = team_id

						// Select this manager
						this.form.manager_id = manager_id

						// Select this broker
						this.form.broker_id = broker_id
					}, 1000)

					return
				}

				if (this.show.teamInput()) this.options.team = await this.getTeams()
				else {
					this.form.team_id = this.user.team.id
					if (this.show.managerInput()) await this.handleManager(this.form.team_id)
					else {
						this.form.manager_id = this.user.manager_id
						await this.handleBroker(this.form.manager_id)
					}
				}
			})

			this.$root.$on('clean--' + this.nuggetTotalName, () => {
				this.form = { ...defaultForm() }
			})
		},

		beforeDestroy() {
			this.$root.$off(this.nuggetTotalName)
			this.$root.$off('clean--' + this.nuggetTotalName)
		},

		methods: {
			async handleBroker(id) {
				this.options.broker = []
				this.form.broker_id = null

				if (!this.$mx_hasValue(id)) return

				this.$mx_setLoader('broker', true)
				this.options.broker = await this.getBrokers(id)
				this.$mx_setLoader('broker', false)
			},

			async handleManager(id) {
				this.options.manager = []
				this.options.broker = []
				this.form.manager_id = null
				this.form.broker_id = null

				if (!this.$mx_hasValue(id)) {
					this.form.team_id = null
					return
				}

				this.$mx_setLoader('manager', true)
				setTimeout(async () => {
					this.options.manager = await this.getManagers(id)
					this.$mx_setLoader('manager', false)
				}, 250)
			},

			async getTeams() {
				try {
					const teams = (await team.getAll('', { nocover: true, per_page: 1000 })).data
					return teams.data.map((option) => ({
						label: option.name,
						value: option.id,
					}))
				} catch (error) {
					this.$mx_alertThisOnError(error)
					return []
				}
			},

			async getManagers(id) {
				try {
					const managers = (await team.getManagersByTeam(id)).data
					return managers.map((option) => ({
						label: option.first_name,
						value: option.manager_id,
					}))
				} catch (error) {
					this.$mx_alertThisOnError(error)
					return []
				}
			},

			async getBrokers(id) {
				try {
					const brokers = (await team.getBrokersByManager(id)).data
					return brokers.map((option) => ({
						label: option.first_name,
						value: option.broker_id,
					}))
				} catch (error) {
					this.$mx_alertThisOnError(error)
					return []
				}
			},
		}
	}
</script>

<style lang="stylus" scoped>
	@import './styles.styl'
</style>
